import { useMemo, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { getAirline, AIRLINE_OPTIONS } from '../../utils/airlines';
import { toDateStr } from '../../utils/dates';
import { getCityName, isValidAirport } from '../../utils/airports';
import { computeFlightStats } from '../../utils/flightStats';
import { computeAchievements } from '../../utils/achievements';
import { renderStoryCard } from '../../utils/renderStoryCard';
import { FlightEntryModal } from '../flight-entry/FlightEntryModal';
import { Plus, Camera, Pencil, ChevronDown, Check, X, Settings, QrCode, Image, History } from 'lucide-react';
import { QRModal } from '../shared/QRModal';
import { FlightStats } from './FlightStats';
import { SettingsScreen } from './SettingsScreen';
import { takePhoto, uploadAvatar } from '../../services/cameraService';
import { ImageCropper } from '../shared/ImageCropper';
import { haptic } from '../../utils/haptics';
import { shareText } from '../../utils/nativeShare';
import { AvatarImg } from '../shared/Avatar';
import { DOWNLOAD_URL } from '../../constants/links';
import type { Flight } from '../../types';

const PAST_IMPORT_DISMISSED_KEY = 'rostfound_past_import_dismissed';

export function ProfileTab() {
  const { currentUser, flights, updateCurrentUser } = useApp();
  const [showFlightEntry, setShowFlightEntry] = useState(false);
  const [editingFlight, setEditingFlight] = useState<Flight | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editAirline, setEditAirline] = useState(currentUser.airlineCode);
  const [editCustomCode, setEditCustomCode] = useState('');
  const [editBase, setEditBase] = useState(currentUser.baseAirport);
  const [saving, setSaving] = useState(false);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [showQR, setShowQR] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [pastImportDismissed, setPastImportDismissed] = useState(
    () => !!localStorage.getItem(PAST_IMPORT_DISMISSED_KEY)
  );
  const globeSnapshotRef = useRef<(() => Promise<Blob>) | null>(null);
  const airline = getAirline(currentUser.airlineCode);

  // Stats for share card
  const today = toDateStr(new Date());
  const pastFlights = useMemo(() => flights.filter((f) => f.date <= today), [flights, today]);
  const stats = useMemo(() => computeFlightStats(pastFlights, currentUser.baseAirport), [pastFlights, currentUser.baseAirport]);
  const achievements = useMemo(() => computeAchievements(stats, 0, pastFlights, 0), [stats, pastFlights]);

  const handleShareStats = async () => {
    haptic();
    setSharing(true);
    try {
      const globeBlob = await globeSnapshotRef.current?.();
      if (!globeBlob) {
        // Fallback to text share if globe not available
        const text = `Add me on Rost & Found! My handle: @${currentUser.handle}\n\nDownload: ${DOWNLOAD_URL}`;
        await shareText({ text });
        return;
      }

      const cardBlob = await renderStoryCard({
        globeBlob,
        userName: currentUser.name,
        handle: currentUser.handle,
        airlineName: airline.name,
        baseAirport: currentUser.baseAirport,
        stats: {
          totalFlights: stats.totalFlights,
          totalNauticalMiles: stats.totalNauticalMiles,
          totalEstimatedHours: stats.totalEstimatedHours,
          uniqueAirportCount: stats.uniqueAirports.length,
          countriesVisited: stats.countriesVisited,
        },
        topAirports: stats.topAirports.slice(0, 5).map((ap) => ({
          iata: ap.iata,
          city: getCityName(ap.iata),
          count: ap.count,
        })),
        achievements: achievements.filter((a) => a.unlocked).map((a) => a.title),
        avatarUrl: currentUser.avatarUrl,
      });

      const file = new File([cardBlob], 'rost-and-found-stats.png', { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'My Flight Stats',
          text: 'Check out my flight stats on Rost & Found!',
        });
      } else {
        // Fallback: download
        const url = URL.createObjectURL(cardBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'rost-and-found-stats.png';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch { /* user cancelled share sheet */ }
    finally { setSharing(false); }
  };

  const isOtherAirline = editAirline === 'OTHER';
  const effectiveCode = isOtherAirline ? editCustomCode.toUpperCase() : editAirline;

  const showPastImportNudge = !pastImportDismissed
    && flights.length > 0
    && pastFlights.length === 0;

  const handleStartEdit = () => {
    const knownCodes = AIRLINE_OPTIONS.map((a) => a.code).filter((c) => c !== 'OTHER');
    if (knownCodes.includes(currentUser.airlineCode)) {
      setEditAirline(currentUser.airlineCode);
      setEditCustomCode('');
    } else {
      setEditAirline('OTHER');
      setEditCustomCode(currentUser.airlineCode);
    }
    setEditBase(currentUser.baseAirport);
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    const code = effectiveCode;
    const base = editBase.trim().toUpperCase();
    if (!code || (isOtherAirline && code.length < 2)) return;
    if (!base || !isValidAirport(base)) return;

    setSaving(true);
    await updateCurrentUser({ airlineCode: code, baseAirport: base });
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="pb-4">
      {/* Profile header */}
      <div
        className="px-4 py-5 text-white bg-gradient-to-br from-slate-800 via-slate-800 to-[color-mix(in_srgb,var(--color-accent-val)_12%,var(--color-slate-800))]"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={async () => {
              try {
                const photo = await takePhoto();
                if (photo) setCropImage(photo);
              } catch (err) {
                console.warn('[ProfileTab] Avatar pick failed:', err);
              }
            }}
            className="relative w-14 h-14 rounded-full bg-accent/20 ring-2 ring-accent/40 flex items-center justify-center text-xl font-bold shrink-0 overflow-hidden"
          >
            <AvatarImg src={currentUser.avatarUrl} name={currentUser.name} />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 active:opacity-100 transition-opacity rounded-full">
              <Camera size={16} className="text-white" />
            </div>
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">{currentUser.name}</h2>
              <button
                data-testid="profile-settings"
                onClick={() => { haptic(); setShowSettings(true); }}
                className="p-1.5 rounded-full bg-accent/15 hover:bg-accent/25 text-accent transition-colors"
              >
                <Settings size={16} />
              </button>
            </div>
            <button
              onClick={() => {
                haptic();
                navigator.clipboard.writeText(`@${currentUser.handle}`);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
              className="text-accent text-sm hover:text-accent-dark transition-colors"
            >
              {copied ? 'Copied!' : `@${currentUser.handle}`}
            </button>
            {!editing && (
              <>
                <div className="flex items-center gap-1 mt-1">
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-semibold"
                    style={{ backgroundColor: airline.color + '20', color: airline.color }}
                  >
                    {airline.name}
                  </span>
                  <span className="text-xs text-white/60 ml-1">
                    Base: <span className="font-mono">{currentUser.baseAirport}</span>
                  </span>
                  <button
                    onClick={handleStartEdit}
                    className="ml-auto p-1 rounded-full bg-white/15 hover:bg-white/25 transition-colors"
                  >
                    <Pencil size={12} />
                  </button>
                </div>
                {currentUser.instagramHandle && (
                  <button
                    onClick={() => window.open(`https://instagram.com/${currentUser.instagramHandle}`, '_blank')}
                    className="flex items-center gap-1 mt-1 text-xs text-white/60 hover:text-white transition-colors"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                    </svg>
                    @{currentUser.instagramHandle}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Inline edit form */}
        {editing && (
          <div className="mt-3 bg-white/10 rounded-lg p-3 space-y-2">
            <div className="relative">
              <select
                value={editAirline}
                onChange={(e) => setEditAirline(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-white/40"
              >
                {AIRLINE_OPTIONS.map((a) => (
                  <option key={a.code} value={a.code} className="bg-slate-800 text-slate-100">
                    {a.name} ({a.code})
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none" />
            </div>
            {isOtherAirline && (
              <input
                type="text"
                maxLength={3}
                value={editCustomCode}
                onChange={(e) => setEditCustomCode(e.target.value.toUpperCase())}
                placeholder="IATA code (e.g. AA)"
                className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-white/40 uppercase"
              />
            )}
            <input
              type="text"
              maxLength={3}
              value={editBase}
              onChange={(e) => setEditBase(e.target.value.toUpperCase())}
              placeholder="Base airport (e.g. FRA)"
              className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-white/40 uppercase font-mono"
            />
            {editBase.length === 3 && (
              isValidAirport(editBase) ? (
                <p className="text-xs text-white/70 mt-0.5">{getCityName(editBase)}</p>
              ) : (
                <p className="text-xs text-red-300 mt-0.5">Not a recognized airport code</p>
              )
            )}
            <div className="flex gap-2">
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-medium transition-colors disabled:opacity-50"
              >
                <Check size={14} />
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 text-sm transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Past import nudge */}
      {showPastImportNudge && (
        <div data-testid="past-import-nudge" className="mx-3 mt-3 bg-slate-800 rounded-xl p-3 border border-accent/20">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-accent/15 flex items-center justify-center shrink-0 mt-0.5">
              <History size={16} className="text-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-100">Complete your flying career</p>
              <p className="text-xs text-slate-400 mt-0.5">Import a past roster to fill your stats, route map, and achievements.</p>
              <div className="flex gap-2 mt-2.5">
                <button
                  onClick={() => { haptic(); setShowFlightEntry(true); }}
                  className="px-3 py-1.5 bg-accent text-white text-xs font-medium rounded-lg active:scale-[0.98] transition-transform"
                >
                  Import Past Roster
                </button>
                <button
                  onClick={() => {
                    localStorage.setItem(PAST_IMPORT_DISMISSED_KEY, '1');
                    setPastImportDismissed(true);
                  }}
                  className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 px-3 mt-3">
        <button
          onClick={() => setShowFlightEntry(true)}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-white font-medium text-sm bg-accent active:bg-accent-dark transition-colors"
        >
          <Plus size={16} />
          Add Flight
        </button>
        <button
          onClick={handleShareStats}
          disabled={sharing}
          className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-800 border border-accent/20 text-accent font-medium text-sm hover:bg-accent/10 active:bg-accent/15 transition-colors disabled:opacity-50"
        >
          {sharing ? (
            <div className="w-4 h-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
          ) : (
            <Image size={16} />
          )}
          Stats
        </button>
        <button
          onClick={() => { haptic(); setShowQR(true); }}
          className="flex items-center justify-center px-3 py-3 rounded-xl bg-slate-800 border border-accent/20 text-accent hover:bg-accent/10 active:bg-accent/15 transition-colors"
        >
          <QrCode size={18} />
        </button>
      </div>

      {/* Flight Map & Stats */}
      <FlightStats globeSnapshotRef={globeSnapshotRef} />

      {/* Settings */}
      <SettingsScreen open={showSettings} onClose={() => setShowSettings(false)} />

      <FlightEntryModal
        open={showFlightEntry || !!editingFlight}
        onClose={() => { setShowFlightEntry(false); setEditingFlight(null); }}
        editFlight={editingFlight ?? undefined}
      />

      {cropImage && (
        <ImageCropper
          image={cropImage}
          onCancel={() => setCropImage(null)}
          onConfirm={async (cropped) => {
            setCropImage(null);
            try {
              const url = await uploadAvatar(currentUser.id, cropped);
              if (url) await updateCurrentUser({ avatarUrl: url });
            } catch (err) {
              console.warn('[ProfileTab] Avatar upload failed:', err);
            }
          }}
        />
      )}

      <QRModal
        handle={currentUser.handle}
        name={currentUser.name}
        open={showQR}
        onClose={() => setShowQR(false)}
      />
    </div>
  );
}
